import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

/**
 * Lightweight confirmation dialog — controlled `open`, replaces `window.confirm`.
 *
 * Usage:
 *   const [pendingDelete, setPendingDelete] = useState(null);
 *   <ConfirmDialog
 *     open={!!pendingDelete}
 *     title="Delete this project?"
 *     description="This action cannot be undone."
 *     confirmLabel="Delete"
 *     destructive
 *     onCancel={() => setPendingDelete(null)}
 *     onConfirm={async () => { await doDelete(pendingDelete); setPendingDelete(null); }}
 *     testId="admin-projects-confirm-delete"
 *   />
 */
export const ConfirmDialog = ({
  open,
  title = 'Are you sure?',
  description = 'This action cannot be undone.',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  onConfirm,
  onCancel,
  testId,
}) => (
  <AlertDialog open={open} onOpenChange={(o) => { if (!o && onCancel) onCancel(); }}>
    <AlertDialogContent data-testid={testId}>
      <AlertDialogHeader>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel
          onClick={onCancel}
          data-testid={testId ? `${testId}-cancel` : undefined}
        >
          {cancelLabel}
        </AlertDialogCancel>
        <AlertDialogAction
          onClick={onConfirm}
          className={destructive ? 'bg-red-600 hover:bg-red-700 text-white' : 'text-white'}
          style={destructive ? {} : { backgroundColor: '#00CED1' }}
          data-testid={testId ? `${testId}-confirm` : undefined}
        >
          {confirmLabel}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);
